import { CollectedItemDetails } from "@apiserver-rest/ponder-data-client-base";
import { FilteredPreviewItem } from "@ponder-shared/tools/filter/filtered-preview-item";
import { FilterType, PreviewItemFilterOption } from "@ponder-shared/tools/filter/preview-item-filter-option";
import { isColorless, isMulticolor } from "../magicCardUtils";

enum MagicCardSortOption {
    Default = "Default",
    Name = "By Name",
    Color = "By Color",
    ValueDesc = "By Value Desc.",
    ValueAsc = "By Value Asc.",
    ValueAnyDesc = "By Value (Any) Desc.",
    ValueAnyAsc = "By Value (Any) Asc.",
    CollectedValueDesc = "Collected Value Desc.",
    CollectedValueAsc = "Collected Value Asc.",
    CollectedCountDesc = "Collected Count Desc.",
    CollectedCountAsc = "Collected Count Asc.",
    ReleaseDesc = "By Release Desc.",
    ReleaseAsc = "By Release Asc.",
    ManaDesc = "By Mana Cost Desc.",
    ManaAsc = "By Mana Cost Asc.",
    PowerDesc = "By Power Desc.",
    PowerAsc = "By Power Asc.",
    ToughnessDesc = "By Toughness Desc.",
    ToughnessAsc = "By Toughness Asc."
}

/**
 * Yea this isn't a "filter" but its easier to shove a one-off filter drop-down into this (for now).
 */
export class MagicCardSort extends PreviewItemFilterOption {
    private currentChoice: MagicCardSortOption = MagicCardSortOption.Default;
    private currentCollectionChoice: MagicCardSortOption = MagicCardSortOption.Default;

    private options = new Map<string, number | undefined>();
    private isDirty: boolean = false;
    
    constructor() {
        super("Sort", FilterType.Sort, "", MagicCardSortOption.Color, MagicCardSortOption.CollectedValueDesc);
    }

    public override prepare(): void {
        
    }

    public override track(data: any, collectedInfo: CollectedItemDetails | undefined): void {
        // ... nothing ...
    }

    public override updateSelection(selection: MagicCardSortOption, page: string): boolean {
        if (this.getCurrentChoice() == selection) {
            return false;
        }

        this.setStoredFilter(selection, page);
        this.setCurrentChoice(selection);

        this.isDirty = true;

        return true;
    }

    private getCurrentChoice(): MagicCardSortOption {
        if (this.isCollection) {
            return this.currentCollectionChoice;
        } else {
            return this.currentChoice;
        }
    }

    private setCurrentChoice(selection: MagicCardSortOption): void {
        if (this.isCollection) {
            this.currentCollectionChoice = selection;
        } else {
            this.currentChoice = selection;
        }
    }

    public override getFilters(): Map<string, number | undefined> {
        return this.options;
    }

    public override isValid(data: any, collectedInfo: CollectedItemDetails | undefined): boolean {
        // ... nothing ...
        return true;
    }

    public override setUserInfo(isCollection: boolean, isAuthed: boolean): void {
        this.isCollection = isCollection;
        this.isAuthed = isAuthed;
        this.buildOptions();
    }

    private buildOptions(): void {
        this.options.clear();

        Object.values(MagicCardSortOption).forEach(o => {
            if (!this.isAuthed) {
                if (o != MagicCardSortOption.CollectedValueDesc && 
                    o != MagicCardSortOption.CollectedValueAsc && 
                    o != MagicCardSortOption.CollectedCountDesc && 
                    o != MagicCardSortOption.CollectedCountAsc) {
                    this.options.set(o, undefined);
                }
            } else {
                this.options.set(o, undefined);
            }
        });
    }

    public sort(items: FilteredPreviewItem[]): void {
        if (!items || !this.isDirty) {
            return;
        }

        switch (this.getCurrentChoice()) {
            case MagicCardSortOption.Name:
                items.sort((a, b) => this.sortByName(a, b));
                break;

            case MagicCardSortOption.Color:
                items.sort((a, b) => this.sortByColor(a, b));
                break;

            case MagicCardSortOption.ValueDesc:
                items.sort((a, b) => this.sortByPrice(a, b, false));
                break;

            case MagicCardSortOption.ValueAsc:
                items.sort((a, b) => this.sortByPrice(a, b, true));
                break;
                break;

            case MagicCardSortOption.ValueAnyDesc:
                items.sort((a, b) => this.sortByPriceAny(a, b, false));
                break;

            case MagicCardSortOption.ValueAnyAsc:
                items.sort((a, b) => this.sortByPriceAny(a, b, true));
                break;

            case MagicCardSortOption.CollectedValueDesc:
                items.sort((a, b) => this.sortByCollectedValue(a, b, false));
                break;

            case MagicCardSortOption.CollectedValueAsc:
                items.sort((a, b) => this.sortByCollectedValue(a, b, true));
                break;

            case MagicCardSortOption.CollectedCountDesc:
                items.sort((a, b) => this.sortByCollectedCount(a, b, false));
                break;

            case MagicCardSortOption.CollectedCountAsc:
                items.sort((a, b) => this.sortByCollectedCount(a, b, true));
                break;
            
            case MagicCardSortOption.ReleaseDesc:
                items.sort((a, b) => this.sortByReleaseDate(a, b, false));
                break;

            case MagicCardSortOption.ReleaseAsc:
                items.sort((a, b) => this.sortByReleaseDate(a, b, true));
                break;

            case MagicCardSortOption.ManaDesc:
                items.sort((a, b) => this.sortByManaCost(a, b, false));
                break;

            case MagicCardSortOption.ManaAsc:
                items.sort((a, b) => this.sortByManaCost(a, b, true));
                break;

            case MagicCardSortOption.PowerDesc:
                items.sort((a, b) => this.sortByPower(a, b, false));
                break;

            case MagicCardSortOption.PowerAsc:
                items.sort((a, b) => this.sortByPower(a, b, true));
                break;

            case MagicCardSortOption.ToughnessDesc:
                items.sort((a, b) => this.sortByToughness(a, b, false));
                break;

            case MagicCardSortOption.ToughnessAsc:
                items.sort((a, b) => this.sortByToughness(a, b, true));
                break;

            case MagicCardSortOption.Default:
            default:
                items.sort((a, b) => (a.originalIndex < b.originalIndex ? -1 : 1));
                break;
        }
    }

    private sortByName(a: FilteredPreviewItem, b: FilteredPreviewItem): number {
        return (a.previewItem.item.name! < b.previewItem.item.name! ? -1 : 1);
    }

    private sortByColor(a: FilteredPreviewItem, b: FilteredPreviewItem): number {
        if (a.previewItem.item.data!.magic!.colorMask == b.previewItem.item.data!.magic!.colorMask) {
            return this.sortByName(a, b);
        }

        var aColorless = isColorless(a.previewItem.item.data!.magic!);
        var aMulticolor = isMulticolor(a.previewItem.item.data!.magic!);

        var bColorless = isColorless(b.previewItem.item.data!.magic!);
        var bMulticolor = isMulticolor(b.previewItem.item.data!.magic!);

        var aMask = (aColorless ? 9999 : aMulticolor ? 999 : a.previewItem.item.data!.magic!.colorMask);
        var bMask = (bColorless ? 9999 : bMulticolor ? 999 : b.previewItem.item.data!.magic!.colorMask);

        return aMask - bMask;
    }

    private sortByReleaseDate(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;
        var aRelease = a.previewItem.item.data!.magic!.releaseDate;
        var bRelease = b.previewItem.item.data!.magic!.releaseDate;

        if (!aRelease) {
            return r;
        } else if (!bRelease) { 
            return -r;
        } else {
            if (aRelease === bRelease) {
                return this.sortByName(a, b);
            } else {
                return (aRelease! > bRelease! ? -r : r);
            }
        }
    }

    private sortByManaCost(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;
        var aManaCost = a.previewItem.item.data!.magic!.manaValue;
        var bManaCost = b.previewItem.item.data!.magic!.manaValue;

        if (!aManaCost) {
            return 1;
        } else if (!bManaCost) {
            return -1;
        } else {
            // Maybe do further sorting to split 0 cost artifacts and lands?
            if (aManaCost == bManaCost) {
                return this.sortByName(a, b);
            } else {
                return (aManaCost < bManaCost ? r : -r);
            }
        }
    }

    private sortByPower(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aPower = a.previewItem.item.data!.magic!.faces![0].power;
        var bPower = b.previewItem.item.data!.magic!.faces![0].power;

        return this.sortByNumberString(a, b, aPower, bPower, r);
    }

    private sortByToughness(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aPower = a.previewItem.item.data!.magic!.faces![0].toughness;
        var bPower = b.previewItem.item.data!.magic!.faces![0].toughness;

        return this.sortByNumberString(a, b, aPower, bPower, r);
    }

    /**
     * Sorts by the normal price of the item. If the item doesn't have a normal price, uses the special price.
     * @param a
     * @param b 
     * @param ascending 
     * @returns 
     */
    private sortByPrice(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aPrice = a.previewItem.price.normalUSD > 0 ? a.previewItem.price.normalUSD : a.previewItem.price.specialUSD;
        var bPrice = b.previewItem.price.normalUSD > 0 ? b.previewItem.price.normalUSD : b.previewItem.price.specialUSD;

        if (aPrice == bPrice) {
            return this.sortByName(a, b);
        } else {
            return (aPrice < bPrice ? r : -r);
        }
    }

    /**
     * Sorts by the highest of any price for the item. So by normal or special price, whichever is greater.
     * @param a 
     * @param b 
     * @param ascending 
     * @returns 
     */
    private sortByPriceAny(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aPrice = a.previewItem.price.normalUSD > a.previewItem.price.specialUSD ? a.previewItem.price.normalUSD : a.previewItem.price.specialUSD;
        var bPrice = b.previewItem.price.normalUSD > b.previewItem.price.specialUSD ? b.previewItem.price.normalUSD : b.previewItem.price.specialUSD;

        if (aPrice == bPrice) {
            return this.sortByName(a, b);
        } else {
            return (aPrice < bPrice ? r : -r);
        }
    }

    private sortByCollectedValue(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aCollectedNormal = a.previewItem.collected?.count ? true : false;
        var aCollectedSpecial = a.previewItem.collected?.specialCount ? true : false;

        var bCollectedNormal = b.previewItem.collected?.count ? true : false;
        var bCollectedSpecial = b.previewItem.collected?.specialCount ? true : false;

        var aPrice = 0;
        var bPrice = 0;

        if (aCollectedNormal && aCollectedSpecial) {
            aPrice = a.previewItem.price.normalUSD < a.previewItem.price.specialUSD ? a.previewItem.price.specialUSD : a.previewItem.price.normalUSD;
        } else if (aCollectedNormal) {
            aPrice = a.previewItem.price.normalUSD;
        } else if (aCollectedSpecial) {
            aPrice = a.previewItem.price.specialUSD;
        }

        if (bCollectedNormal && bCollectedSpecial) {
            bPrice = b.previewItem.price.normalUSD < b.previewItem.price.specialUSD ? b.previewItem.price.specialUSD : b.previewItem.price.normalUSD;
        } else if (bCollectedNormal) {
            bPrice = b.previewItem.price.normalUSD;
        } else if (bCollectedSpecial) {
            bPrice = b.previewItem.price.specialUSD;
        }

        if (aPrice == bPrice) {
            return this.sortByName(a, b);
        } else {
            return (aPrice < bPrice ? r : -r);
        }
    }

    private sortByCollectedCount(a: FilteredPreviewItem, b: FilteredPreviewItem, ascending: boolean): number {
        var r = ascending ? -1 : 1;

        var aCollectedCount = (a.previewItem.collected?.count ?? 0) + (a.previewItem.collected?.specialCount ?? 0);
        var bCollectedCount = (b.previewItem.collected?.count ?? 0) + (b.previewItem.collected?.specialCount ?? 0);

        if (aCollectedCount == bCollectedCount) {
            return this.sortByName(a, b);
        }
        else {
            return (bCollectedCount - aCollectedCount) * r;
        }
    }

    private sortByNumberString(a: FilteredPreviewItem, b:FilteredPreviewItem, aNumberStr: string | undefined, bNumberStr: string | undefined, r: number) {
        // number "strings" can be ints, floats, or things like "1+*" or even the infinity symbol.
        if (!aNumberStr) {
            return r;
        } else if (!bNumberStr) {
            return -r;
        } else {
            var aNumber = parseFloat(aNumberStr);
            var bNumber = parseFloat(bNumberStr);

            if (aNumber == bNumber) {
                return this.sortByName(a, b);
            } else {
                if (isNaN(aNumber)) {
                    return r;
                } else if (isNaN(bNumber)) {
                    return -r;
                } else {
                    return (aNumber < bNumber ? r : -r);
                }
            }
        }
    }
}